import { com } from '@eidu/web'
import { kindDefinitions, type KnownKindKey } from '../domain/kindRegistry'
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const KIND_ORDER: readonly string[] = [
  'User',
  'School',
  'LearningUnit',
  'Form',
  'Dataset',
  'Chart',
  'Dashboard',
  'Runtime',
  'Feature',
  'Config',
  'Generic',
]

export const kindLabel = (kindKey: string): string =>
  (kindDefinitions as Record<string, { label: string } | undefined>)[kindKey as KnownKindKey]?.label ?? kindKey

const kindOrderIndex = (kindKey: string): number => {
  const i = KIND_ORDER.indexOf(kindKey)
  return i === -1 ? KIND_ORDER.length : i
}

export const compareKindKeys = (a: string, b: string): number => {
  const da = kindOrderIndex(a)
  const db = kindOrderIndex(b)
  if (da !== db) return da - db
  return a.localeCompare(b)
}

/**
 * Groups entity types by their kind. Returns a list of groups in the canonical kind order;
 * within each group, entries preserve the input order.
 */
export const groupEntityTypesByKind = <T>(
  entries: readonly T[],
  getEntityType: (entry: T) => EntityType
): { kindKey: string; label: string; entries: T[] }[] => {
  const byKind = new Map<string, T[]>()
  for (const entry of entries) {
    const k = String(getEntityType(entry).kind)
    const list = byKind.get(k) ?? []
    list.push(entry)
    byKind.set(k, list)
  }
  return Array.from(byKind.keys())
    .sort(compareKindKeys)
    .map((kindKey) => ({ kindKey, label: kindLabel(kindKey), entries: byKind.get(kindKey) ?? [] }))
}
